import { useRef } from 'react'
import { useForm } from 'react-hook-form'
import { useState } from 'react'
import StructuredData from '../components/StructuredData'
import './EpoxyCountertopsLandingPage.css'

interface QuoteFormData {
  name: string
  email: string
  phone: string
  company?: string
  referral?: string
  projectType: string
}

function EpoxyCountertopsLandingPage() {
  const formRef = useRef<HTMLDivElement>(null)
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [isSubmitError, setIsSubmitError] = useState(false)
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<QuoteFormData>()

  const scrollToForm = () => {
    formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  const FORMSPREE_ENDPOINT = 'https://formspree.io/f/mojnjjob'

  const onSubmit = async (data: QuoteFormData) => {
    try {
      const res = await fetch(FORMSPREE_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: data.name,
          email: data.email,
          phone: data.phone,
          company: data.company || '',
          referral: data.referral || '',
          projectType: data.projectType,
          _subject: 'Epoxy Countertop Quote Request',
        }),
      })
      if (!res.ok) throw new Error('Submit failed')
      setIsSubmitted(true)
      reset()
      setTimeout(() => setIsSubmitted(false), 8000)
    } catch {
      setIsSubmitError(true)
      setTimeout(() => setIsSubmitError(false), 5000)
    }
  }

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: 'Epoxy Resin Lab Countertops & Work Surfaces',
    description:
      'High-performance epoxy resin countertops and work surfaces for laboratories. Chemical-resistant, heat-resistant, Class A fire-rated, and UL Greenguard Gold certified.',
    brand: { '@type': 'Brand', name: 'Laboratory Specialists' },
    offers: { '@type': 'Offer', priceCurrency: 'USD', availability: 'https://schema.org/UponRequest' },
  }

  return (
    <div className="epoxy-landing-page">
      <StructuredData data={structuredData} />

      {/* Hero */}
      <section className="epoxy-hero">
        <div className="epoxy-hero-bg" aria-hidden />
        <div className="container epoxy-hero-inner">
          <h1 className="epoxy-hero-title">
            Epoxy Countertops Built for the Harshest Lab Environments
          </h1>
          <p className="epoxy-hero-subtitle">
            Chemical-resistant, heat-resistant, and Class A fire-rated epoxy resin work surfaces—supplied and installed by laboratory specialists. Trusted in university STEM labs, K–12 science classrooms, and industrial facilities.
          </p>
          <div className="epoxy-hero-badges">
            <span className="epoxy-badge">SEFA 3 Tested</span>
            <span className="epoxy-badge">UL Greenguard Gold</span>
            <span className="epoxy-badge">Class A Fire-Rated</span>
            <span className="epoxy-badge">ISO 9001:2015</span>
          </div>
          <div className="epoxy-hero-cta">
            <button type="button" className="epoxy-cta-button" onClick={scrollToForm}>
              Get Your Free Quote
            </button>
            <p className="epoxy-hero-cta-note">Custom quote in 24 hours · Free samples available · No obligation</p>
          </div>
        </div>
      </section>

      {/* Why epoxy */}
      <section className="epoxy-why">
        <div className="container">
          <h2 className="epoxy-section-title">Why Labs Choose Epoxy Resin Work Surfaces</h2>
          <p className="epoxy-section-intro">
            Not all countertop materials survive a lab environment. Epoxy resin is the industry standard for a reason.
          </p>
          <div className="epoxy-why-grid">
            <div className="epoxy-why-card">
              <div className="epoxy-why-icon" aria-hidden>🧪</div>
              <h3>Superior Chemical Resistance</h3>
              <p>
                Withstands exposure to acids, solvents, bases, and staining agents that destroy standard countertop materials. SEFA 3 tested and certified.
              </p>
            </div>
            <div className="epoxy-why-card">
              <div className="epoxy-why-icon" aria-hidden>🔥</div>
              <h3>Heat & Fire Resistance</h3>
              <p>
                Class A fire rating. Handles open flames, hot plates, and autoclave splash without warping, cracking, or discoloring.
              </p>
            </div>
            <div className="epoxy-why-card">
              <div className="epoxy-why-icon" aria-hidden>🛡️</div>
              <h3>Stain, Pit & Mar Resistant</h3>
              <p>
                High-density construction resists scratches, pitting, and permanent staining—keeping surfaces cleanable and presentable for the life of your lab.
              </p>
            </div>
            <div className="epoxy-why-card">
              <div className="epoxy-why-icon" aria-hidden>🌿</div>
              <h3>Certified Safe Indoors</h3>
              <p>
                UL Greenguard Gold certified for low chemical emissions. SCS certified. NSF/ANSI 51 approved for food service applications.
              </p>
            </div>
            <div className="epoxy-why-card">
              <div className="epoxy-why-icon" aria-hidden>🇺🇸</div>
              <h3>USA-Made, ISO Certified</h3>
              <p>
                Manufactured in the United States at an ISO 9001:2015 certified facility. Consistent quality with every order.
              </p>
            </div>
            <div className="epoxy-why-card">
              <div className="epoxy-why-icon" aria-hidden>🔧</div>
              <h3>Complete System</h3>
              <p>
                Beyond countertops—drop-in and undermount sinks, pegboards, drip trays, outlets, troughs, and color-matched caulk. One system, one supplier.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Product lines */}
      <section className="epoxy-products">
        <div className="container">
          <h2 className="epoxy-section-title">Two Product Lines to Match Your Lab</h2>
          <div className="epoxy-products-grid">
            <div className="epoxy-product-card epoxy-product-durcon">
              <h3>Durcon Epoxy</h3>
              <p className="epoxy-product-tagline">The standard-setter in laboratory epoxy resin</p>
              <p>
                The original and most widely specified epoxy resin worksurface in the industry. Used in university chemistry labs, pharmaceutical facilities, and industrial environments where chemical exposure is severe and unrelenting.
              </p>
              <ul>
                <li>Available in standard and custom sizes</li>
                <li>Compatible with all major casework systems</li>
                <li>Full accessory ecosystem: sinks, drip trays, pegboards</li>
                <li>SEFA 3, ASTM, and ASHRAE tested</li>
              </ul>
              <button type="button" className="epoxy-product-cta" onClick={scrollToForm}>
                Get a Quote
              </button>
            </div>
            <div className="epoxy-product-card epoxy-product-greenstone">
              <h3>Greenstone Epoxy</h3>
              <p className="epoxy-product-tagline">All the performance, more color options</p>
              <p>
                The same proven epoxy resin chemistry as Durcon, offered in an expanded color palette for labs where design and aesthetics matter alongside performance—science classrooms, medical training facilities, and modern research spaces.
              </p>
              <ul>
                <li>Environmentally friendly LEED product</li>
                <li>Broader color range for design flexibility</li>
                <li>Identical chemical and heat resistance to Durcon</li>
                <li>Same certifications: Greenguard Gold, SEFA 3, NSF/ANSI 51</li>
                <li>Color-matched caulk and accessories available</li>
              </ul>
              <button type="button" className="epoxy-product-cta" onClick={scrollToForm}>
                Get a Quote
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Applications */}
      <section className="epoxy-applications">
        <div className="container">
          <h2 className="epoxy-section-title">Where We Install Epoxy Work Surfaces</h2>
          <ul className="epoxy-app-list">
            <li>University and college chemistry, biology, and STEM laboratories</li>
            <li>K–12 science classrooms and teaching labs</li>
            <li>Pharmaceutical and biotech research facilities</li>
            <li>Industrial quality control and testing labs</li>
            <li>Medical and clinical laboratory environments</li>
            <li>Food science and NSF-compliant food service spaces</li>
          </ul>
        </div>
      </section>

      {/* Objections */}
      <section className="epoxy-objections">
        <div className="container">
          <ul className="epoxy-bullets">
            <li>
              <strong>Replacing damaged countertops?</strong> We handle retrofit and replacement installations—existing casework doesn't need to be rebuilt to get new epoxy surfaces.
            </li>
            <li>
              <strong>Not sure which product line is right?</strong> We'll walk you through Durcon vs. Greenstone based on your chemical exposure, budget, and design requirements—and send free samples so you can see and feel the material before you commit.
            </li>
            <li>
              <strong>Need sinks and accessories too?</strong> We supply the complete system—worksurfaces, drop-in sinks, troughs, and drip trays—so everything fits and matches.
            </li>
          </ul>
        </div>
      </section>

      {/* Quote form */}
      <section className="epoxy-form-section" ref={formRef} id="quote-form">
        <div className="container">
          <div className="epoxy-form-card">
            <h2 className="epoxy-form-title">Get Your Free Epoxy Countertop Quote</h2>
            <p className="epoxy-form-lead-magnet">
              Tell us about your project and we'll send a <strong>custom quote</strong> within 24 hours. <strong>Free samples</strong> available on request—no obligation.
            </p>

            {isSubmitted ? (
              <div className="epoxy-form-success">
                <h3>Thanks—we'll be in touch within 24 hours.</h3>
                <p>We'll email or call with your quote and next steps.</p>
              </div>
            ) : (
              <form className="epoxy-form" onSubmit={handleSubmit(onSubmit)}>
                {isSubmitError && (
                  <p className="epoxy-form-error" role="alert">
                    Something went wrong. Please try again or call us at (541) 658-9374.
                  </p>
                )}

                <div className="epoxy-form-group">
                  <label htmlFor="epoxy-name">
                    Full Name <span className="epoxy-required">*</span>
                  </label>
                  <input
                    type="text"
                    id="epoxy-name"
                    {...register('name', {
                      required: 'Name is required',
                      minLength: { value: 2, message: 'Please enter your full name' },
                    })}
                    aria-invalid={errors.name ? 'true' : 'false'}
                    placeholder="First and Last Name"
                  />
                  {errors.name && (
                    <span className="epoxy-error" role="alert">{errors.name.message}</span>
                  )}
                </div>

                <div className="epoxy-form-group">
                  <label htmlFor="epoxy-email">
                    Email <span className="epoxy-required">*</span>
                  </label>
                  <input
                    type="email"
                    id="epoxy-email"
                    {...register('email', {
                      required: 'Email is required',
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: 'Please enter a valid email',
                      },
                    })}
                    aria-invalid={errors.email ? 'true' : 'false'}
                    placeholder="Email Address"
                  />
                  {errors.email && (
                    <span className="epoxy-error" role="alert">{errors.email.message}</span>
                  )}
                </div>

                <div className="epoxy-form-group">
                  <label htmlFor="epoxy-phone">
                    Phone <span className="epoxy-required">*</span>
                  </label>
                  <input
                    type="tel"
                    id="epoxy-phone"
                    {...register('phone', {
                      required: 'Phone is required',
                      pattern: {
                        value: /^[\d\s\-+()]+$/,
                        message: 'Please enter a valid phone number',
                      },
                    })}
                    aria-invalid={errors.phone ? 'true' : 'false'}
                    placeholder="123-456-7890"
                  />
                  {errors.phone && (
                    <span className="epoxy-error" role="alert">{errors.phone.message}</span>
                  )}
                </div>

                <div className="epoxy-form-group">
                  <label htmlFor="epoxy-company">Company / Institution (optional)</label>
                  <input
                    type="text"
                    id="epoxy-company"
                    {...register('company')}
                    placeholder="Your lab or organization"
                  />
                </div>

                <div className="epoxy-form-group">
                  <label htmlFor="epoxy-referral">How did you hear about us? (optional)</label>
                  <input
                    type="text"
                    id="epoxy-referral"
                    {...register('referral')}
                    placeholder="e.g. Google, referral, trade show..."
                  />
                </div>

                <div className="epoxy-form-group">
                  <label htmlFor="epoxy-projectType">
                    What do you need? <span className="epoxy-required">*</span>
                  </label>
                  <select
                    id="epoxy-projectType"
                    {...register('projectType', { required: 'Please select an option' })}
                    aria-invalid={errors.projectType ? 'true' : 'false'}
                  >
                    <option value="">Select...</option>
                    <option value="new-lab">New lab epoxy countertops</option>
                    <option value="replacement">Replacement / retrofit countertops</option>
                    <option value="durcon">Durcon epoxy specifically</option>
                    <option value="greenstone">Greenstone epoxy specifically</option>
                    <option value="sinks-accessories">Epoxy sinks and accessories</option>
                    <option value="full-system">Full countertop + sink system</option>
                    <option value="not-sure">Not sure yet—need guidance</option>
                  </select>
                  {errors.projectType && (
                    <span className="epoxy-error" role="alert">{errors.projectType.message}</span>
                  )}
                </div>

                <p className="epoxy-privacy">
                  By submitting you agree to our{' '}
                  <a href="/privacy" target="_blank" rel="noopener noreferrer">
                    Privacy Policy
                  </a>
                  . We won't share your information.
                </p>

                <button type="submit" className="epoxy-submit" disabled={isSubmitting}>
                  {isSubmitting ? 'Sending...' : 'Get My Free Quote and Samples'}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* Legal */}
      <section className="epoxy-legal">
        <div className="container">
          <p className="epoxy-legal-text">
            Laboratory Specialists. Epoxy resin countertop supply and installation. Durcon and Greenstone are products of Durcon, a business unit of Wilsonart Engineered Surfaces.{' '}
            <a href="/privacy">Privacy Policy</a> · <a href="/contact">Contact</a>
          </p>
        </div>
      </section>
    </div>
  )
}

export default EpoxyCountertopsLandingPage
